import {
  RefundShippingCostPolicy,
  RefundType,
  ReturnType,
} from "~/common/types/return";
import { OrderItem } from "~/common/types/order";
import { InitiatedReturnItem } from "~/hooks/useReturnState/useReturnState.types";
import { getPaidUnitPrice } from "~/util/getPaidUnitPrice";

export const getIsIncludeOriginalShippingCostInRefund = (
  refundShippingCostPolicy: RefundShippingCostPolicy,
  orderItemsQuantity: number,
  registeredItemsQuantity: number,
  initiatedItems: InitiatedReturnItem[],
  refundType: RefundType
): boolean => {
  if (refundShippingCostPolicy === RefundShippingCostPolicy.Refund) {
    return true;
  }
  if (refundShippingCostPolicy === RefundShippingCostPolicy.NoRefund) {
    return false;
  }

  // Check if any ineligible items exist
  const hasIneligibleItems = initiatedItems.some(
    (item) =>
      [ReturnType.Refund, ReturnType.Exchange].includes(item.returnType) &&
      refundType === RefundType.ShopExchange
  );

  // If ineligible items exist, no refund on shipping
  if (hasIneligibleItems) {
    return false;
  }

  // The backend never refunds original shipping on a return with an exchange (`skip_refund_of_shipping_cost?`).
  if (hasExchangeInitiatedReturnItems(initiatedItems)) {
    return false;
  }
  const initiatedItemsQuantity = initiatedItems.reduce(
    (acc, curr) => acc + curr.returnQuantity,
    0
  );
  return (
    initiatedItemsQuantity + registeredItemsQuantity === orderItemsQuantity
  );
};

export const getRefundInitiatedReturnItems = (
  initiatedItems: InitiatedReturnItem[]
) =>
  initiatedItems.filter(({ returnType }) => returnType === ReturnType.Refund);

export const hasExchangeInitiatedReturnItems = (
  initiatedItems: InitiatedReturnItem[]
) =>
  initiatedItems.some(({ returnType }) => returnType === ReturnType.Exchange);

// The replacement price comes from the backend per returned line (discount carry-over
// already applied), against what the customer paid per unit for that line.
export const getExchangeDeltaTotal = (
  initiatedItems: InitiatedReturnItem[],
  orderItems: OrderItem[],
  allowExchangePriceDifference = false
) => {
  if (!allowExchangePriceDifference) {
    return 0;
  }

  const total = initiatedItems.reduce((total, initiatedItem) => {
    if (
      initiatedItem.returnType !== ReturnType.Exchange ||
      !initiatedItem.exchangeVariant?.id
    ) {
      return total;
    }

    const orderItem = orderItems.find(
      ({ id }) => id === initiatedItem.orderItemId
    );

    if (!orderItem) {
      return total;
    }

    return (
      total +
      (Number(initiatedItem.exchangeVariant.replacementPrice ?? 0) -
        getPaidUnitPrice(orderItem)) *
        Number(initiatedItem.returnQuantity ?? 0)
    );
  }, 0);

  // Round to 2dp to match the other monetary helpers in this file and to
  // avoid float drift like 0.1 + 0.2 = 0.30000000000000004 leaking into
  // the charge and payment copy decisions downstream.
  return parseFloat(total.toFixed(2));
};

// The backend settles an exchange without the store credit bonus, so the bonus never pays towards a charge.
export const getExchangeSettlementCharge = ({
  hasCartItems,
  settlesExchangePriceDifference,
  refundAmountWithoutBonus,
}: {
  hasCartItems: boolean;
  settlesExchangePriceDifference: boolean;
  refundAmountWithoutBonus: number;
}) => {
  const owesExchangeSettlement =
    !hasCartItems &&
    settlesExchangePriceDifference &&
    refundAmountWithoutBonus < 0;

  return owesExchangeSettlement ? Math.abs(refundAmountWithoutBonus) : 0;
};

export const getExchangeChargeExplanationKey = ({
  exchangeDeltaTotal,
  returnCost,
}: {
  exchangeDeltaTotal: number;
  returnCost: number;
}) => {
  if (exchangeDeltaTotal <= 0) {
    return "return_fee_payment_explanation";
  }

  return returnCost > 0
    ? "exchange_and_return_fee_payment_explanation"
    : "exchange_payment_explanation";
};
